import React, {ReactNode} from 'react'
import {QuestionStatusIcon, QuestionStatusItem} from './QuestionStatus'
import {ProfileUI} from 'components/portal/profileTypes'
import {usePortal} from 'components/portal/portal'
import {SAvatar} from 'components/portal/SAvatar'
import {StyleProps} from 'shared/ui'
import {isDueStatus, QuestionCfg} from 'shared/forecast/question'
import {SubqCfg} from 'shared/forecast/subq'
import {entries, makeTree, MakeTreeNode} from 'shared/collection'
import {fmtDate, FmtDateOptions} from 'shared/format'
import {MasonryGrid} from 'shared/ui'
import {ParticipationStatus, ParticipationStatusProps, TaskedStatus} from './Participation'
import {DueLabel} from './Due'
import Link from 'next/link'
import {SubqTypeIcon} from './Subqs'
import {first, sortBy} from 'remeda'
import {PublishStatus} from './QuestionPublish'
import {twMerge} from 'tailwind-merge'
import {DateTime} from 'luxon'
import {HiArrowRight, HiUser, HiUserGroup} from 'react-icons/hi'
import {Tooltip} from '@mantine/core'
import {QuestionPageRow} from './useQuestions'
import {ForecastProgress} from './progress'
import {UseParticipation} from './participation'

type QuestionTileItemsProps = {
  statusItem?: QuestionStatusItem
  profile?: ProfileUI
  due?: string
  participation?: UseParticipation
  publish?: {
    id: string
    publish?: QuestionCfg['publish']
    liveView?: boolean
  }
  progress?: ForecastProgress
} & Partial<Pick<QuestionPageRow, 'forecasters_total' | 'forecasts_from' | 'forecasts_to' | 'created_at'>> &
  StyleProps

/** The extra's shared by all types of questions on all pages */
export const QuestionTileItems = ({
  statusItem,
  profile,
  created_at,
  due,
  participation,
  progress,
  publish,
  forecasters_total,
  forecasts_from,
  forecasts_to
}: QuestionTileItemsProps) => {
  const status = statusItem?.value
  const pubLatest = first(
    sortBy(
      entries(publish?.publish?.publications ?? {})
        .map(([ts, pub]) => ({ts: Number.parseInt(ts), ...pub}))
        .filter(p => p.published),
      [({ts}) => ts, 'desc']
    )
  )
  const {
    workspace_id,
    workspace: {advancedUI}
  } = usePortal()
  return (
    <>
      {participation && advancedUI && <ParticipationStatus participation={participation} />}
      {statusItem && <QuestionStatusIcon item={statusItem} />}
      {profile && <SAvatar profile={profile} size='sm' />}
      {publish && (
        <div className='flex gap-[2px]'>
          {pubLatest && <PublishStatus id={publish.id} type='published' ts={pubLatest.ts} />}
          {publish?.liveView && workspace_id && (
            <PublishStatus id={publish.id} type='liveView' workspace_id={workspace_id} />
          )}
        </div>
      )}
      {(forecasters_total != null || forecasts_from || forecasts_to) && (
        <QuestionTileForecastDates {...{forecasters_total, forecasts_from, forecasts_to, created_at}} />
      )}
      {due && (statusItem == null || isDueStatus(statusItem.value)) && <DueLabel date={due} />}
      {participation?.isTasked && progress && advancedUI && (
        <TaskedStatus isTasked={participation.isTasked} progress={progress} />
      )}
    </>
  )
}

export const QuestionTileForecastDates = ({
  forecasters_total,
  forecasts_from,
  forecasts_to,
  created_at
}: Pick<QuestionTileItemsProps, 'forecasters_total' | 'forecasts_from' | 'forecasts_to' | 'created_at'>) => (
  <Tooltip
    label={
      <div className='text-sm flex flex-col gap-1'>
        <div>
          <b>{forecasters_total}</b>
          <span className='opacity-70'> users who have made forecasts</span>
        </div>
        {forecasts_from && <LabelDate date={forecasts_from} prefix='first forecast' options={{hideMatching: false}} />}
        {forecasts_to && <LabelDate date={forecasts_to} prefix='last forecast made' options={{hideMatching: false}} />}
        {!forecasts_from && !forecasts_to && created_at && (
          <div className='flex gap-0 items-center text-sm'>
            <LabelDate date={DateTime.fromISO(created_at)} prefix='question created' options={{hideMatching: false}} />
          </div>
        )}
      </div>
    }>
    <div className='flex items-center gap-1 whitespace-nowrap'>
      {forecasters_total != null && (
        <div className='flex gap-0 items-center text-sm'>
          <div className='font-extrabold'>{forecasters_total}</div>
          <div className='opacity-50 '>
            <HiUser />
          </div>
        </div>
      )}
      {forecasts_from && forecasts_to && <LabelDateRange from={forecasts_from} to={forecasts_to} />}
      {!forecasts_from && !forecasts_to && created_at && (
        <div className='flex gap-0 items-center text-sm'>
          <LabelDate date={DateTime.fromISO(created_at)} prefix='created' />
        </div>
      )}
    </div>
  </Tooltip>
)

const LabelDate = ({
  date,
  prefix,
  suffix,
  options
}: {
  date: DateTime
  prefix?: ReactNode
  suffix?: ReactNode
  options?: FmtDateOptions
}) => (
  <div className='flex text-sm gap-1 items-center'>
    {prefix && <div className='opacity-70'>{prefix}</div>}
    <div className='font-extrabold opacity-90'>{fmtDate(date, {hideMatching: true, ...options})}</div>
    {suffix && <div className='opacity-70 self-center'>{suffix}</div>}
  </div>
)

const LabelDateRange = ({from, to}: {from: DateTime; to: DateTime}) => {
  const isSame = fmtDate(from) == fmtDate(to)
  return (
    <div className='flex text-sm gap-1 items-center'>
      <LabelDate
        date={from}
        prefix={isSame ? 'on' : 'from'}
        options={{hideMatching: to, hideMatchingMode: isSame ? 'year' : 'year-month'}}
      />
      {!isSame && (
        <>
          <div className='opacity-50'>
            <HiArrowRight size={9} />
          </div>
          <LabelDate date={to} options={{hideMatching: true}} />
        </>
      )}
    </div>
  )
}

export type SubqItem = {id: string; title: ReactNode | string; parent_id?: string} & Pick<SubqCfg, 'subquestionType'>

export const QuestionSubquestions = ({
  question_id,
  subquestions
}: {
  question_id: string
  subquestions: Array<SubqItem>
}) => {
  const roots = makeTree(subquestions, s => s.parent_id)
  return (
    <MasonryGrid className='my-2' colWidth='18em'>
      {roots
        ?.filter(s => !s.parent_id)
        .map(s => (
          <div key={s.id}>
            <SubqLink question_id={question_id} {...s} />
          </div>
        ))}
    </MasonryGrid>
  )
}

const SubqLink = ({
  id,
  title,
  children,
  question_id,
  subquestionType
}: MakeTreeNode<SubqItem> & {question_id: string}) => (
  <div className='space-y-[2px]'>
    <Link className='' href={`/question/${question_id}?subquestion=${id}`}>
      <SubqTypeIcon type={subquestionType} />
      {title}
    </Link>
    <div className='ml-2 flex flex-col'>
      {children?.map(c => (
        <SubqLink key={c.id} question_id={question_id} {...c} />
      ))}
    </div>
  </div>
)

export const QuestionHeader = ({
  title,
  subTitle,
  className,
  classNames
}: {
  title: ReactNode | string
  subTitle?: ReactNode | string
  className?: string
  classNames?: {title?: string; subTitle?: string; root?: string}
}) => (
  <div className={twMerge('', className, classNames?.root)}>
    <h3 className={twMerge('text-balance', classNames?.title)}>{title}</h3>
    <div className={twMerge('text-subtle', classNames?.subTitle)}>{subTitle}</div>
  </div>
)
